import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { MessageSquare } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { DateTimePicker } from '@/components/common/DateTimePicker';
import { useInteractionMutations } from '../hooks';
import { INTERACTION_TYPE_VALUES } from '../types';
import type { InteractionEntry, InteractionType, Matter } from '../types';

interface InteractionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  matter: Matter | null;
  /** Existing auto-drafted entry to complete; null/undefined = create new. */
  entry?: InteractionEntry | null | undefined;
  /** Pre-selected type for a new entry (e.g. COURT_HEARING from Gate 3). */
  defaultType?: InteractionType | undefined;
}

/**
 * Case 360° (Stage 7) — add / complete an Interaction Log entry. Shared by
 * `InteractionLogCard` and the Gate-3 transition dialog (logging the missing
 * entry without leaving the gate).
 */
export function InteractionDialog({
  open,
  onOpenChange,
  matter,
  entry,
  defaultType = 'MEETING',
}: InteractionDialogProps) {
  const { t } = useTranslation();
  const { createInteractionMutation, updateInteractionMutation } =
    useInteractionMutations();

  const [type, setType] = React.useState<InteractionType>(defaultType);
  const [date, setDate] = React.useState('');
  const [summary, setSummary] = React.useState('');

  // Seed once per open — a background refetch must not wipe in-progress input.
  React.useEffect(() => {
    if (open) {
      setType(entry?.type ?? defaultType);
      setDate(
        entry?.date
          ? format(new Date(entry.date), 'yyyy-MM-dd')
          : format(new Date(), 'yyyy-MM-dd'),
      );
      setSummary(entry?.summary ?? '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const isPending =
    createInteractionMutation.isPending || updateInteractionMutation.isPending;
  const canSubmit = !!summary.trim() && !isPending;

  const handleSubmit = async () => {
    if (!canSubmit || !matter) return;
    if (entry) {
      await updateInteractionMutation.mutateAsync({
        entryId: entry.id,
        matterId: matter.id,
        data: {
          type,
          ...(date ? { date: new Date(date).toISOString() } : {}),
          summary: summary.trim(),
        },
      });
    } else {
      await createInteractionMutation.mutateAsync({
        id: matter.id,
        data: {
          type,
          ...(date ? { date: new Date(date).toISOString() } : {}),
          summary: summary.trim(),
        },
      });
    }
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MessageSquare className="h-4 w-4 text-slate-500" />
            {entry
              ? t('interaction.completeDialogTitle')
              : t('interaction.addDialogTitle')}
          </DialogTitle>
          <DialogDescription>
            {t('interaction.dialogDescription')}
          </DialogDescription>
        </DialogHeader>

        <div className="my-2 space-y-4">
          <div>
            <label className="mb-2 block text-sm font-medium">
              {t('interaction.typeLabel')}
            </label>
            <Select value={type} onValueChange={(v) => setType(v as InteractionType)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {INTERACTION_TYPE_VALUES.map((it) => (
                  <SelectItem key={it} value={it}>
                    {t(`interaction.type.${it}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <label className="mb-2 block text-sm font-medium">
              {t('interaction.dateLabel')}
            </label>
            <DateTimePicker
              mode="date"
              value={date || undefined}
              onChange={(d) => setDate(d ? format(d, 'yyyy-MM-dd') : '')}
            />
          </div>

          <div>
            <label className="mb-2 block text-sm font-medium">
              {t('interaction.summaryLabel')}
            </label>
            <Textarea
              value={summary}
              onChange={(e) => setSummary(e.target.value)}
              placeholder={t('interaction.summaryPlaceholder')}
              rows={4}
              maxLength={4000}
            />
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isPending}
          >
            {t('cancel')}
          </Button>
          <Button onClick={handleSubmit} disabled={!canSubmit}>
            {isPending ? t('processing') : t('interaction.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
